// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { ToastrService } from "ngx-toastr";
import { Observable, Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { PropertyRemappingsService } from "src/app/modules/properties/property-remappings.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * Modal form to create OR edit a property remapping (plant + description).
 * The dialog owns the "plants" fetch so the required "Impianto" select is
 * never rendered empty. Both add (POST) and edit (PATCH) persist immediately.
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-add-remapping-dialog",
  templateUrl: "./add-remapping-dialog.component.html",
  styleUrls: ["./add-remapping-dialog.component.scss"],
})
export class AddRemappingDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};

  isEditMode = false;
  dialogIsLoading = true;

  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.add"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: true,
    mode: "indeterminate",
    buttonIcon: { fontSet: "fal", fontIcon: "fa-save", inline: true },
  };

  constructor(
    public dialogRef: MatDialogRef<AddRemappingDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private propertyRemappingsService: PropertyRemappingsService
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.isEditMode = !!(this.data && this.data.remapping);
    this.formBtnOptions.text = this.translateService.instant(
      this.isEditMode ? "common.cta.save" : "common.cta.add"
    );
    // The submit button stays disabled until the plant options are loaded, so
    // the required "Impianto" select can never be presented with zero options.
    this.formBtnOptions.disabled = true;

    this.subscriptions.push(
      this.propertyRemappingsService.getPlants().subscribe(
        (res) => {
          const plants =
            res && res.status && Array.isArray(res.data) ? res.data : [];
          this.initForm(plants);
          this.dialogIsLoading = false;
          if (plants.length > 0) {
            this.formBtnOptions.disabled = false;
          } else {
            // No selectable plant: keep submit disabled and warn the user.
            this.toastrService.error(
              this.translateService.instant(
                "properties.remappings.errors.plantsNotLoaded"
              )
            );
          }
        },
        (err) => {
          this.helperService.handleError(err);
          this.dialogIsLoading = false;
          // Submit stays disabled: nothing to select.
        }
      )
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onDenial(): void {
    this.dialogRef.close();
  }

  onFormSubmit(model: any): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      return;
    }

    const body = {
      plant: model.plant,
      description: model.description,
    };
    const request: Observable<any> = this.isEditMode
      ? this.propertyRemappingsService.update(
          this.data.propertyId,
          this.data.remapping._id,
          body
        )
      : this.propertyRemappingsService.create(this.data.propertyId, body);

    this.formBtnOptions.active = true;
    this.subscriptions.push(
      request
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          (res) => {
            if (res && res.status) {
              this.toastrService.success(res.message);
              this.dialogRef.close(true);
            }
          },
          (err) => {
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  initForm(plants: any[]): void {
    const plantOptions = plants.map((plant: any) => {
      return { value: plant.key, label: plant.label };
    });

    this.model = this.isEditMode
      ? {
          plant: this.data.remapping.plant,
          description: this.data.remapping.description,
        }
      : {};

    this.fields = [
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "select",
            key: "plant",
            templateOptions: {
              label: this.translateService.instant(
                "properties.remappings.fields.plant"
              ),
              placeholder: this.translateService.instant(
                "properties.remappings.fields.plant"
              ),
              required: true,
              options: plantOptions,
            },
          },
          {
            className: "col-12",
            type: "textarea",
            key: "description",
            templateOptions: {
              label: this.translateService.instant(
                "properties.remappings.fields.description"
              ),
              placeholder: this.translateService.instant(
                "properties.remappings.fields.description"
              ),
              required: true,
              minLength: 1,
              maxLength: 2000,
              rows: 4,
            },
          },
        ],
      },
    ];
  }
}
